import React, { useState, useEffect, FC, Fragment } from "react";
import { ReactComponent as ExitIcon } from "@images/exit_icon.svg";
import { useHotkeys } from "react-hotkeys-hook";
import IconButton from "@components/common/IconButton";
import { useAuth } from "@providers/AuthContext";
import { Dialog, Transition } from "@headlessui/react";
import { changeEmail } from "@utils/managers/networking/NetworkManager";
import LoadingView, { LoadingState } from "@components/common/LoadingView";
import { generateToast } from "@utils/managers/ToastManager";
import { kErrorConstants } from "@utils/constants/ErrorConstants";
import { kStringConstants } from "@utils/constants/StringConstants";

interface ChangeEmailModalProps {
	isShow: boolean;
	onClose: () => void;
}

function checkEmail(email: string) {
	const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
	return emailRegex.test(email) && email !== "";
}

function normalizeEmail(email: string) {
	return email.trim().toLowerCase();
}

const ChangeEmailModal: FC<ChangeEmailModalProps> = ({ isShow, onClose }) => {
	const { authUser, setAuthUser } = useAuth();
	const [newEmail, setNewEmail] = useState("");
	const [isValidEmail, setIsValidEmail] = useState(false);
	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: false,
	});

	useHotkeys("Escape", () => onModalClose());

	useEffect(() => {
		if (isShow) {
			setNewEmail("");
			setIsValidEmail(false);
		}
	}, [isShow]);

	const onModalClose = () => {
		onClose();
	};

	const emailInputOnChange = (e: React.FormEvent<HTMLInputElement>) => {
		const emailValue = e.currentTarget.value;
		setNewEmail(emailValue);
		setIsValidEmail(checkEmail(emailValue.trim()));
	};

	const isUnchangedEmail = authUser ? normalizeEmail(newEmail) === normalizeEmail(authUser.email) : true;
	const canSave = isValidEmail && !isUnchangedEmail && !loadingState.isLoading;

	const saveOnClick = () => {
		if (!canSave) {
			return;
		}

		setLoadingState({ isLoading: true, message: "Updating email" });

		changeEmail(newEmail)
			.then((profile) => {
				setAuthUser(profile);
				setLoadingState({ isLoading: false });
				onModalClose();
				generateToast({ status: "success", message: "Email updated", position: "bottom-center" });
			})
			.catch((error) => {
				setLoadingState({ isLoading: false });

				let title = kStringConstants.Common.errorAlertTitle;
				let message = kStringConstants.Common.errorAlertMessage;

				if (error === kErrorConstants.signUp.existingAccount) {
					title = "Email already in use";
					message = error.message;
				} else if (error.response?.data?.message) {
					message = error.response.data.message;
				}

				generateToast({ status: "error", title, message });
			});
	};

	return (
		<>
			<LoadingView loadingState={loadingState} />
			<Transition as={Fragment} show={isShow}>
				<Dialog onClose={() => onClose()} className="relative z-40">
					<Transition.Child as="div" enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
						<div className="fixed inset-0 bg-black bg-opacity-70" aria-hidden="true" />
					</Transition.Child>
					<div className="fixed inset-0 flex w-screen items-center justify-center p-4">
						<Transition.Child className="w-full h-full" enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
							<Dialog.Panel className="m-auto w-full h-full max-w-lg transform overflow-hidden rounded-2xl bg-secondary border border-primary-200 text-left align-middle shadow-xl transition-all">
								<div className="absolute w-full bg-surface backdrop-blur-md top-0 left-0 flex flex-row justify-between items-center p-4 pb-3 border-b border-primary-200 z-20">
									<div className="text-primary font-medium font-primary text-xl">Change Email</div>
									<IconButton Icon={ExitIcon} className="-mr-2.5" onClick={() => onModalClose()} />
								</div>
								<div className="flex flex-col gap-4 text-left h-full p-4 py-[105px] bg-secondary overflow-auto w-full">
									<div className="font-primary font-regular text-sm text-primary-500">
										Current email: <span className="text-primary font-medium">{authUser?.email}</span>
									</div>
									<div className="flex flex-col gap-2">
										<div className="font-primary font-medium text-sm text-primary">New email</div>
										<input
											type="email"
											value={newEmail}
											onChange={emailInputOnChange}
											placeholder="Enter new email address"
											autoFocus={true}
											className="w-full bg-surface border border-primary-200 rounded-xl px-3 py-3 text-primary font-regular font-primary text-base"
										/>
									</div>
									<div className="font-primary font-regular text-xs text-primary-500">
										We'll use this email for your Meco account sign-in and account updates. Your connected mailbox addresses won't change.
									</div>
								</div>
								<div className="absolute bottom-0 w-full bg-surface backdrop-blur-md border-t border-primary-100 px-4 py-4 pt-3 z-20">
									<div className="flex flex-row justify-end">
										<button
											disabled={!canSave}
											className={`${canSave ? "opacity-100" : "opacity-50"} text-white font-medium font-primary text-base focus:outline-none rounded-xl p-2 px-3 py-2 items-center bg-success-green transition ease-in-out whitespace-nowrap`}
											onClick={() => saveOnClick()}
										>
											Save
										</button>
									</div>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</Dialog>
			</Transition>
		</>
	);
};

export default ChangeEmailModal;
